import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './index.css';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './components/Home';
import AboutUs from './components/AboutUs';
import HowToOrder from './components/HowToOrder';
import Products from './components/Products';
import QuickEnquiry from './components/QuickEnquiry';
import OrderTrack from './components/OrderTrack';
import Contact from './components/Contact';
import SafetyTips from './components/SafetyTips';
import PriceList from './components/PriceList';
import TermsConditions from './components/TermsConditions';
import UnderMaintenance from './components/UnderMaintenance';
import WelcomePopup from './components/WelcomePopup';
import { OrdersClosedBanner, OrdersClosedModal } from './components/OrdersClosed';
import { getSite, refreshSite, resolveAssetUrl, prefetchAll } from './services/api';

const CLOSED_POPUP_KEY = 'orders_closed_popup_seen';
const ORDERS_STATUS_POLL_MS = 5 * 60 * 1000;

function App() {
  const [site, setSite] = useState(null);
  const [siteLoading, setSiteLoading] = useState(true);
  const [siteError, setSiteError] = useState(false);
  const [showClosedPopup, setShowClosedPopup] = useState(false);

  // Missing field (older backend) means orders are open
  const ordersEnabled = site?.orders_enabled !== false;
  const closedMessage = site?.orders_closed_message;

  useEffect(() => {
    let cancelled = false;
    // prefetchAll fires all static API calls in parallel and populates cache.
    // The site itself is fetched fresh so orders_enabled is never stale;
    // the cached copy is only a fallback if that request fails. It is sent
    // before the prefetch burst so the loader isn't queued behind it.
    const sitePromise = refreshSite().catch(() => getSite());
    prefetchAll();
    sitePromise
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) {
          setSite(res.data);
        } else {
          setSiteError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setSiteError(true);
      })
      .finally(() => {
        if (!cancelled) setSiteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The admin can open/close orders at any time — re-check periodically and
  // whenever the tab regains focus.
  useEffect(() => {
    const sync = () => {
      refreshSite()
        .then((res) => {
          if (!res?.success || !res.data) return;
          setSite((prev) =>
            prev &&
            prev.orders_enabled === res.data.orders_enabled &&
            prev.orders_closed_message === res.data.orders_closed_message
              ? prev
              : res.data
          );
        })
        .catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(sync, ORDERS_STATUS_POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, []);

  // Show the closed popup once per session (again if the message changes)
  useEffect(() => {
    if (ordersEnabled) {
      setShowClosedPopup(false);
      return;
    }
    let seen = null;
    try { seen = sessionStorage.getItem(CLOSED_POPUP_KEY); } catch {}
    if (seen !== (closedMessage || '')) setShowClosedPopup(true);
  }, [ordersEnabled, closedMessage]);

  const closeClosedPopup = () => {
    try { sessionStorage.setItem(CLOSED_POPUP_KEY, closedMessage || ''); } catch {}
    setShowClosedPopup(false);
  };

  // Called when POST /orders is rejected with 403 — orders closed while the page was open
  const handleOrdersClosed = useCallback((message) => {
    setSite((prev) => ({
      ...prev,
      orders_enabled: false,
      orders_closed_message: message || prev?.orders_closed_message || null,
    }));
    setShowClosedPopup(true);
  }, []);

  useEffect(() => {
    if (!site) return;

    if (site.title) {
      document.title = site.title;
    }

    if (site.nav_icon) {
      let favicon = document.querySelector("link[rel~='icon']");
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = resolveAssetUrl(site.nav_icon);
    }
  }, [site]);

  if (siteLoading) {
    return (
      <div className="page-loader">
        <span className="firework firework-1" />
        <span className="firework firework-2" />
        <span className="firework firework-3" />
        <span className="firework firework-4" />
        <span className="firework firework-5" />
        <span className="firework firework-6" />
        <img
          src={site?.logo ? resolveAssetUrl(site.logo) : '/images/logo.svg'}
          alt={site?.name || 'Logo'}
          className="page-loader-logo"
        />
        {/* <span className="page-loader-spinner" /> */}
      </div>
    );
  }

  if (siteError) {
    return <UnderMaintenance />;
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      {ordersEnabled ? (
        <WelcomePopup />
      ) : (
        showClosedPopup && <OrdersClosedModal message={closedMessage} onClose={closeClosedPopup} />
      )}
      <Header site={site} />
      <main className="main">
        {!ordersEnabled && <OrdersClosedBanner message={closedMessage} onDetails={() => setShowClosedPopup(true)} />}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/order"
            element={<QuickEnquiry ordersEnabled={ordersEnabled} onOrdersClosed={handleOrdersClosed} />}
          />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/how-to-order" element={<HowToOrder />} />
          <Route path="/products" element={<Products />} />
          <Route path="/order-track" element={<OrderTrack />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/safety-tips" element={<SafetyTips />} />
          <Route path="/price-list" element={<PriceList />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
        </Routes>
      </main>
      <Footer />
      <a
        className="whatsapp-float"
        href="https://api.whatsapp.com/send?phone=917339331711"
        target="_blank"
        rel="noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <i className="fab fa-whatsapp" />
      </a>
    </BrowserRouter>
  );
}

export default App;
